import { memo, useCallback } from 'react';
import {
  useForm,
  FieldType,
  validateMaxLength,
  validateNumbersString,
  validatePhoneByCountryCode
} from 'src/hooks/useForm';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  useCallbackCheckForBonusToActivate,
  useGetFullInfo,
  useGetUserCountry,
  useUpdateInfo
} from 'react-easyrocket';
import { Button } from 'src/components/Buttons/Button';
import { Input } from 'src/components/Inputs/Input';
import { useModalContext } from 'src/hooks/useModalContext';
import { useRoute } from 'src/router/hooks';
import { addressRegex, cityRegex, postCodeRegex } from 'src/constants/regex';
import { InputPhone } from 'src/components/Inputs/InputPhone';
import { getPhoneNumberInfo } from 'src/components/Inputs/InputPhone/utils/getPhoneNumberInfo';
import { CountryCode } from 'libphonenumber-js';

interface StepThreeProps {
  disableFields?: boolean;
}

type ModalProps = {
  onAuthFlowSuccess?: () => void;
};

const _StepThree = ({ disableFields = false }: StepThreeProps) => {
  const { ROUTE } = useRoute();
  const { closeModal, modalProps } = useModalContext<ModalProps>();
  const activateBonus = useCallbackCheckForBonusToActivate();
  const { userCountry, isLoading: isLoadingUserCountry } = useGetUserCountry();

  const handleChangeStep = useCallback(() => {
    closeModal();
    modalProps?.onAuthFlowSuccess?.();
    activateBonus();
  }, [activateBonus, closeModal]);

  const { mutateAsync, isLoading } = useUpdateInfo({ onSuccess: handleChangeStep });

  const { t } = useTranslation();

  const {
    values,
    errors,
    fieldsProps: { addressProps, cityProps, postcodeProps, phoneNumberProps },
    touched,
    setValues
  } = useForm<{
    address: FieldType.Text;
    city: FieldType.Text;
    postcode: FieldType.Text;
    phoneNumber: FieldType.Text;
  }>({
    address: {
      placeholder: `${t('general.fullAddress')}`,
      fieldType: FieldType.Text,
      validation: [validateMaxLength(256)]
    },
    city: {
      placeholder: `${t('general.city')}`,
      fieldType: FieldType.Text,
      validation: [validateMaxLength(256)]
    },
    postcode: {
      placeholder: `${t('general.postCode')}`,
      fieldType: FieldType.Text,
      validation: [validateMaxLength(100)]
    },
    phoneNumber: {
      placeholder: `${t('general.phoneNumber')}`,
      fieldType: FieldType.Text,
      validation: [validateMaxLength(20), validateNumbersString, validatePhoneByCountryCode]
    }
  });

  const handleSubmit = useCallback(() => {
    mutateAsync({
      address: values.address ? values.address.replace(/' '/g, '') : undefined,
      postcode: values.postcode ? values.postcode.replace(/' '/g, '') : undefined,
      city: values.city ? values.city.replace(/' '/g, '') : undefined,
      code:
        values?.phoneNumber === ''
          ? undefined
          : getPhoneNumberInfo(values?.phoneNumber)?.countryCallingCode,
      phone:
        values?.phoneNumber === ''
          ? undefined
          : getPhoneNumberInfo(values?.phoneNumber)?.nationalNumber
    });
  }, [mutateAsync, values.address, values.city, values.postcode, values.phoneNumber]);

  useGetFullInfo({
    onSuccess: (response) => {
      setValues({
        address: response.data.address,
        city: response.data.city,
        postcode: response.data.postcode,
        phoneNumber: response.data.phone.number
      });
    }
  });

  return (
    <form
      className="flex flex-col gap-8 justify-between"
      onSubmit={(e) => e.preventDefault()}
      autoComplete="off">
      <div className="flex flex-col w-full gap-2">
        <div className="w-full">
          <Input
            {...addressProps}
            autoComplete="off"
            regexAllowedSymbols={addressRegex}
            disabled={disableFields}
          />
          {touched.address && errors.address && (
            <span className="text-sum_text_input_error text-xs">{t(errors.address)}</span>
          )}
        </div>
        <div className="flex w-full justify-center gap-3">
          <div>
            <Input
              {...cityProps}
              autoComplete="off"
              regexAllowedSymbols={cityRegex}
              disabled={disableFields}
            />
            {touched.city && errors.city && (
              <span className="text-sum_text_input_error text-xs">{t(errors.city)}</span>
            )}
          </div>
          <div>
            <Input
              {...postcodeProps}
              autoComplete="off"
              regexAllowedSymbols={postCodeRegex}
              disabled={disableFields}
            />
            {touched.postcode && errors.postcode && (
              <span className="text-sum_text_input_error text-xs">{t(errors.postcode)}</span>
            )}
          </div>
        </div>
        <div className="flex w-full items-start justify-between gap-x-3">
          <InputPhone
            isLoading={isLoadingUserCountry}
            phone={phoneNumberProps.value}
            countryCode={userCountry?.country_alpha2 as CountryCode}
            onChange={phoneNumberProps.onChange}
          />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <Button
          loading={isLoading}
          className="!min-w-full"
          mode="button-primary"
          onClick={handleSubmit}
          title={`${t('registration.getAGift')}`}>
          {t('registration.getAGift')}
        </Button>
        <p className="w-full font-medium text-xs m-auto gap-2 text-center text-sum_text">
          <Trans
            i18nKey="registration.youCanAlsoFillInfo"
            components={{
              '1': <span className="text-xs" />,
              '2': (
                <Link
                  to={ROUTE.ACCOUNT_PROFILE_MAIN.get()}
                  className="font-medium cursor-pointer text-sum_login underline text-xs"
                  onClick={() => {
                    closeModal();
                    activateBonus();
                  }}
                  aria-label="profile main"
                />
              )
            }}
          />
        </p>
      </div>
    </form>
  );
};

export const StepThree = memo(_StepThree);
